import ApiService from './ApiService'
import { endpoints } from './Endpoints'
const STORAGE_KEY = 'userConfig'
const SYNC_DEBOUNCE = 3000
const SYNC_NS = '_sync'

class UserConfigService {
    constructor() {
        this._configs = {}
        this._dirty = {}
        this._syncTimer = null
        this._initialized = false
        this._load()
    }

    _load() {
        try {
            this._configs = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
        } catch {
            this._configs = {}
        }
    }

    _save() {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this._configs))
    }

    init(handler) {
        this._handler = handler
        this._initialized = true
        this._pullDone = false
        this._pullPromise = this._pull().then(() => { this._pullDone = true })
    }

    whenReady() {
        return this._pullPromise || Promise.resolve()
    }

    // --- Sync mode ---

    getSyncMode() {
        return this._configs[SYNC_NS]?.mode || 'local'
    }

    setSyncMode(mode) {
        this.setKey(SYNC_NS, 'mode', mode)
    }

    // --- Global config ---

    get(namespace) {
        return this._configs[namespace] ? { ...this._configs[namespace] } : {}
    }

    set(namespace, data) {
        if (!this._configs[namespace]) this._configs[namespace] = {}
        Object.assign(this._configs[namespace], data)
        this._save()
        this._markDirty(namespace)
    }

    getKey(namespace, key, defaultValue) {
        const ns = this._configs[namespace]
        if (!ns || !(key in ns)) return defaultValue
        return ns[key]
    }

    setKey(namespace, key, value) {
        if (!this._configs[namespace]) this._configs[namespace] = {}
        this._configs[namespace][key] = value
        this._save()
        this._markDirty(namespace)
    }

    remove(namespace) {
        delete this._configs[namespace]
        this._save()
    }

    // --- Company-scoped config ---

    _companyNs(namespace) {
        const contextId = this._handler?.getContextId?.() || null
        return `company:${contextId}:${namespace}`
    }

    getCompany(namespace) {
        return this.get(this._companyNs(namespace))
    }

    setCompany(namespace, data) {
        this.set(this._companyNs(namespace), data)
    }

    getCompanyKey(namespace, key, defaultValue) {
        return this.getKey(this._companyNs(namespace), key, defaultValue)
    }

    setCompanyKey(namespace, key, value) {
        this.setKey(this._companyNs(namespace), key, value)
    }

    removeCompany(namespace) {
        this.remove(this._companyNs(namespace))
    }

    // --- Sync ---

    _markDirty(namespace) {
        this._dirty[namespace] = true
        this._scheduleSyncToBackend()
    }

    _scheduleSyncToBackend() {
        clearTimeout(this._syncTimer)
        this._syncTimer = setTimeout(() => this._syncToBackend(), SYNC_DEBOUNCE)
    }

    async _syncToBackend() {
        const dirtyNamespaces = Object.keys(this._dirty)
        if (!dirtyNamespaces.length) return

        const dirtyData = {}
        for (const ns of dirtyNamespaces) {
            dirtyData[ns] = this._configs[ns] || {}
        }
        this._dirty = {}

        try {
            await ApiService.axios.post(endpoints.user_config, {
                configs: dirtyData,
            })
        } catch {
            /* ignore */
        }
    }

    async _pull() {
        try {
            const res = await ApiService.axios.get(endpoints.user_config, {
                _ignoreErrors: ['not_found'],
            })
            if (res?.data?.data?.configs) {
                const remote = res.data.data.configs

                if (remote[SYNC_NS]) {
                    this._configs[SYNC_NS] = remote[SYNC_NS]
                }

                const syncMode = this._configs[SYNC_NS]?.mode || 'local'

                for (const ns in remote) {
                    if (ns === SYNC_NS) continue

                    if (!this._configs[ns]) {
                        this._configs[ns] = remote[ns]
                    } else if (syncMode === 'remote') {
                        Object.assign(this._configs[ns], remote[ns])
                    } else {
                        for (const key in remote[ns]) {
                            if (!(key in this._configs[ns])) {
                                this._configs[ns][key] = remote[ns][key]
                            }
                        }
                    }
                }
                this._save()
            }
        } catch {
            /* ignore */
        }
    }
}

export default new UserConfigService()
